import React, { useState, useMemo, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { AlertCircle, CheckCircle2, Check, FileText } from 'lucide-react'
import type { Case, CaseDocument } from '../../hooks/useCase'
import { mapCaseDocumentToClassification } from '../../hooks/useCase'
import type { DocumentClassification } from '../../types/documentScanning'
import { DocumentUploadArea } from './DocumentUploadArea'
import { DocumentList } from './DocumentList'
import { ScanStatusPanel } from './ScanStatusPanel'
import { DocumentViewerPanel } from './DocumentViewerPanel'
import { ApprovalPanel } from './ApprovalPanel'
import { WorkflowPromptModal } from './WorkflowPromptModal'
import { useDocumentUpload } from './hooks/useDocumentUpload'
import { useDocumentViewer } from './hooks/useDocumentViewer'
import { useWorkflowStage } from './hooks/useWorkflowStage'
import { useDocumentFilters } from './hooks/useDocumentFilters'
import { useScanControls } from './hooks/useScanControls'
import { functionsClient } from '../../lib/functionsClient'
import styles from '../../pages/DocumentsPage.module.css'

const MAX_FILE_SIZE = 25 * 1024 * 1024 // 25 MB

export interface DocumentsViewProps {
  caseData: Case
  caseId: string
}

export const DocumentsView: React.FC<DocumentsViewProps> = ({ caseData, caseId }) => {
  const navigate = useNavigate()
  const [isApproving, setIsApproving] = useState(false)
  const [showWorkflowPrompt, setShowWorkflowPrompt] = useState(false)



  // Hooks
  const { stage, scanStatus, scanError, statusMessage } = useWorkflowStage(caseData)
  const { searchQuery, setSearchQuery, filteredDocuments } = useDocumentFilters(caseData?.documents)
  
  const uploadedCount = (caseData?.documents?.length || 0)
  const { uploadQueue, handleFilesSelected, isUploading } = useDocumentUpload(caseId, uploadedCount)
  
  const { viewingDocument, isLoadingDoc, handleViewDocument, handleCloseViewer } = useDocumentViewer(
    caseId,
    caseData?.documents,
    false // caseLoading not needed here
  )

  // Derive classifications from documents subcollection
  const classifications: DocumentClassification[] = useMemo(() => {
    return (caseData?.documents || [])
      .map(mapCaseDocumentToClassification)
      .filter((classification): classification is DocumentClassification => 
        classification !== null)
  }, [caseData?.documents])

  // Get current document's classification (if any) - derived from classifications array
  const currentClassification = useMemo<DocumentClassification | null>(() => {
    if (!viewingDocument?.filename) return null
    return classifications.find(c => c.filename === viewingDocument.filename) || null
  }, [viewingDocument?.filename, classifications])

  // Handle document selection from list
  const handleDocumentSelect = async (docId: string) => {
    const doc = caseData.documents?.find(d => d.id === docId)
    if (doc) {
      await handleViewDocument(doc)
    }
  }

  // Approve classifications
  const handleApprove = async () => {
    if (!caseId) return

    setIsApproving(true)
    try {
      await functionsClient.approveClassifications(caseId)
      // Success - Firestore will update status to 'approved'
      // Show workflow prompt after Firestore confirms approval
      setShowWorkflowPrompt(true)
    } catch (error) {
      console.error('Approve failed:', error)
    } finally {
      setIsApproving(false)
    }
  }

  // Navigate to income verification
  const handleContinueToIncome = () => {
    navigate(`/quick-check/${caseId}/income`)
  }

  const canStartScan = caseData?.documents?.length > 0 && !isUploading && scanStatus !== 'scanning'

  return (
    <div className={styles.container}>
      {/* Two Column Layout */}
      <div className={styles.twoColumnLayout}>
        {/* Left Column - Single Panel with Upload + Documents */}
        <div className={`${styles.leftColumn} ${!viewingDocument ? styles.leftColumnExpanded : ''}`}>
          <div className={styles.sidebarPanel}>
            {/* Compact Upload Area */}
            <DocumentUploadArea
              onFilesSelected={handleFilesSelected}
              uploadQueue={uploadQueue}
              maxSize={MAX_FILE_SIZE}
            />


            {/* Document List */}
            <DocumentList
              documents={filteredDocuments}
              selectedDocId={viewingDocument?.docId}
              onDocumentSelect={handleDocumentSelect}
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
            />

            {/* Scan Action */}
            <ScanStatusPanel
              stage={stage}
              caseId={caseId}
              caseData={caseData}
              scanStatus={scanStatus}
              statusMessage={statusMessage}
              canStartScan={canStartScan}
              hasClassifications={classifications.length > 0}
            />
          </div>
        </div>

        {/* Right Column - Document Viewer + Classification */}
        <div className={styles.rightColumn}>
          {viewingDocument ? (
            <DocumentViewerPanel
              viewingDocument={viewingDocument}
              onClose={handleCloseViewer}
              caseId={caseId}
              classification={currentClassification}
              stage={stage}
              onApprove={handleApprove}
              onContinue={handleContinueToIncome}
              isApproving={isApproving}
              classificationsCount={classifications.length}
            />
          ) : (
            /* No Document Selected - Show Empty State */
            <div className={styles.emptyViewer}>
              <div className={styles.emptyViewerContent}>
                <FileText size={64} className={styles.emptyIcon} />
                <h3>Select a Document</h3>
                <p>Click on a document from the list to view and classify it.</p>
                {scanError && (
                  <div className={styles.errorBanner}>
                    <AlertCircle size={16} />
                    <span>{scanError}</span>
                  </div>
                )}

                {/* Status Messages */}
                {stage === 'review' && classifications.length > 0 && (
                  <div className={styles.statusSection}>
                    <div className={styles.statusBanner}>
                      <CheckCircle2 size={18} />
                      <span>{classifications.length} documents classified</span>
                    </div>
                    <ApprovalPanel
                      stage={stage}
                      onApprove={handleApprove}
                      onContinue={handleContinueToIncome}
                      isApproving={isApproving}
                    />
                  </div>
                )}

                {(stage === 'approved' || stage === 'approved_with_new') && (
                  <div className={styles.statusSection}>
                    <div className={styles.successBanner}>
                      <Check size={18} />
                      <span>Classifications approved!</span>
                    </div>
                    <button className={styles.continueBtn} onClick={handleContinueToIncome}>
                      Continue to Income
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Workflow Selection Prompt */}
      <WorkflowPromptModal
        isOpen={showWorkflowPrompt}
        onClose={() => setShowWorkflowPrompt(false)}
        onContinueToIncome={handleContinueToIncome}
      />
    </div>
  )
}

